/**
 * Config half of the add-tile dialog (S6): title, tile-type cards (the single
 * type-choosing surface, suggestion-aware), the per-type section, and the
 * live tile preview (what the tile will look like, from the last Preview
 * run's rows). Refresh cadence lives in the data pane, beside the trust
 * strip that reacts to it.
 */
import type { ReactNode } from 'react';
import type { ColumnTypeInfo } from '../../../charts/types/chartTypes';
import { Label } from '@/components/ui/label';
import type { DashboardTileType } from '../../types/dashboardTypes';
import type { ShapeSuggestions } from '../../utils/shapeSuggest';
import type { AddTileFormValues } from './addTileFormModel';
import { AddTilePreview } from './AddTilePreview';
import type { TilePreview } from './useTilePreview';
import { TileTypeCards } from './TileTypeCards';
import { ChartConfigSection } from './ChartConfigSection';
import { StatConfigSection } from './StatConfigSection';
import {
  TableConfigSection,
  PivotConfigSection,
  MapConfigSection,
  NoteConfigSection,
} from './SimpleConfigSections';

export function AddTileConfigPane({
  values,
  set,
  columnInfos,
  spatialColumnNames,
  suggestions,
  onSelectType,
  title,
  preview,
}: {
  values: AddTileFormValues;
  set: (patch: Partial<AddTileFormValues>) => void;
  columnInfos: ColumnTypeInfo[];
  /** Detected geometry columns of the picked preview set (map section). */
  spatialColumnNames: string[];
  /** Shape suggestions from the last preview — badges + click auto-config. */
  suggestions: ShapeSuggestions | null;
  /** Dialog-owned: applies a suggestion's patch when one fits, else sets the type. */
  onSelectType: (type: DashboardTileType) => void;
  title: ReactNode;
  /** Last Preview run — feeds the live tile preview (never re-executed here). */
  preview: TilePreview;
}) {
  const sectionProps = { values, set, columnInfos };
  return (
    <div className="flex min-h-0 flex-col gap-2.5">
      <div className="grid grid-cols-[92px_1fr] items-center gap-2">
        <Label className="text-xs text-muted-foreground">Title</Label>
        {title}
      </div>
      <TileTypeCards selected={values.tileType} suggestions={suggestions} onSelect={onSelectType} />
      {values.tileType === '' && (
        <p className="text-xs text-muted-foreground" data-testid="add-tile-type-hint">
          Run Preview and we&apos;ll suggest the best tile type — or pick one to configure it
          manually.
        </p>
      )}
      {values.tileType === 'chart' && <ChartConfigSection {...sectionProps} />}
      {values.tileType === 'stat' && <StatConfigSection {...sectionProps} />}
      {values.tileType === 'table' && <TableConfigSection {...sectionProps} />}
      {values.tileType === 'pivot' && <PivotConfigSection {...sectionProps} />}
      {values.tileType === 'map' && (
        <MapConfigSection {...sectionProps} spatialColumnNames={spatialColumnNames} />
      )}
      {values.tileType === 'note' && <NoteConfigSection {...sectionProps} />}
      <AddTilePreview values={values} preview={preview} />
    </div>
  );
}
